import React, { useState, useMemo, useCallback } from "react";

const TAG_PALETTE = [
    "#855cd6",
    "#e74c3c",
    "#3498db",
    "#2ecc71",
    "#f39c12",
    "#9b59b6",
    "#1abc9c",
    "#e67e22",
    "#34495e",
    "#ff6b6b",
];

const getTagColor = (tag) => {
    let h = 0;
    for (const c of tag) h = c.charCodeAt(0) + ((h << 5) - h);
    return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
};

const Icon = ({ path, size = 16, ...p }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        {...p}
    >
        {Array.isArray(path) ? (
            path.map((d, i) => <path key={i} d={d} />)
        ) : (
            <path d={path} />
        )}
    </svg>
);

const ICONS = {
    lock: {
        path: ["M7 11V7a5 5 0 0 1 10 0v4", "M3 11h18v11H3z"],
        size: 18,
    },
    unlock: {
        path: ["M7 11V7a5 5 0 0 1 9.9-1", "M3 11h18v11H3z"],
        size: 18,
    },
    trash: {
        path: [
            "M3 6h18",
            "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
            "M10 11v6",
            "M14 11v6",
        ],
        size: 18,
    },
    search: {
        path: [
            "M11 11m-8 0a8 8 0 1 0 16 0a8 8 0 1 0 -16 0",
            "M21 21l-4.35-4.35",
        ],
        size: 16,
    },
    tag: {
        path: "M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z M7 7h.01",
        size: 16,
    },
    list: { path: ["M20 7H4", "M15 12H4", "M9 17H4"], size: 16 },
    info: {
        path: [
            "M12 12v4",
            "M12 8h.01",
            "M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z",
        ],
        size: 16,
    },
    note: {
        path: [
            "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z",
            "M14 2v6h6",
            "M16 13H8",
            "M16 17H8",
            "M10 9H8",
        ],
        size: 14,
    },
    pencil: {
        path: "M15 3L21 9L9 21L3 21L3 15L15 3Z",
        size: 18,
    },
    pencilOff: {
        path: ["M15 3L21 9L9 21L3 21L3 15L15 3Z", "M3 3L21 21"],
        size: 18,
    },
    replay: {
        path: [
            "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8",
            "M3 3v5h5",
            "M12 7v5l4 2",
        ],
        size: 18,
    },
};

const TagChip = React.memo(
    ({ tag, isPrimary, roomId, onRemove, onMakePrimary }) => {
        const c = getTagColor(tag);
        return (
            <span
                className="room-tag-chip"
                style={{
                    backgroundColor: c + "18",
                    color: c,
                    border: `1px solid ${c}40`,
                    fontWeight: isPrimary ? 700 : 500,
                    boxShadow: isPrimary ? `0 0 0 2px ${c}40` : "none",
                }}
            >
                {isPrimary && "★ "}
                {tag}
                <span className="tag-actions">
                    {!isPrimary && onMakePrimary && (
                        <button
                            className="tag-make-primary"
                            onClick={() => onMakePrimary(roomId, tag)}
                            title="Agrupar"
                        >
                            ☆
                        </button>
                    )}
                    {onRemove && (
                        <button
                            className="tag-remove"
                            onClick={() => onRemove(roomId, tag)}
                            title="Eliminar"
                        >
                            ✕
                        </button>
                    )}
                </span>
            </span>
        );
    },
);

const SearchBar = ({ q, onQ }) => (
    <div className="search-row">
        <div style={{ position: "relative", width: 280 }}>
            <div
                style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                }}
            >
                <Icon {...ICONS.search} stroke="#94a3b8" />
            </div>
            <input
                type="text"
                value={q}
                onChange={(e) => onQ(e.target.value)}
                placeholder="Nombre, etiqueta o sala"
                className="search-input"
                title="Buscar por nombre de alumno, etiqueta o sala."
            />
            {q && (
                <button className="search-clean" onClick={() => onQ("")}>
                    ✕
                </button>
            )}
        </div>
    </div>
);

const RoomCard = React.memo(({ room, ...p }) => {
    const {
        id,
        name,
        isPrivate,
        locked = false,
        tags = [],
        students = [],
        notes = "",
    } = room;
    const {
        activeTagInput,
        tagDraft,
        setTagDraft,
        setActiveTagInput,
        onAddTag,
        onRemoveTag,
        onMakeTagPrimary,
        onToggleLock,
        onToggleRoomLock,
        onDeleteRoom,
        onReplayRoom,
        onRemoveStudent,
        handleStudentInputChange,
        handleAddStudent,
        openCsvImporter,
        newStudentInputs,
        roomNotes,
        handleNoteChange,
        handleNoteBlur,
        collapsedNotes,
        setCollapsedNotes,
    } = p;

    return (
        <div className="room-card">
            <div className="room-card-header">
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 6,
                    }}
                >
                    <span className="room-title">{name}</span>
                    {locked && (
                        <span
                            className="room-locked-badge"
                            title="Sala bloqueada"
                        >
                            <Icon {...ICONS.lock} size={14} />
                        </span>
                    )}
                    <span
                        className={`room-visibility-badge ${isPrivate ? "private" : "public"}`}
                    >
                        {isPrivate ? "Privada" : "Pública"}
                    </span>
                    {tags.map((t, i) => (
                        <TagChip
                            key={t}
                            tag={t}
                            isPrimary={i === 0}
                            roomId={id}
                            onRemove={onRemoveTag}
                            onMakePrimary={onMakeTagPrimary}
                        />
                    ))}
                    {tags.length < 5 &&
                        (activeTagInput === id ? (
                            <input
                                autoFocus
                                className="tag-inline-input"
                                value={tagDraft}
                                onChange={(e) => setTagDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        const c = tagDraft.trim();
                                        if (c) onAddTag(id, c);
                                        setTagDraft("");
                                        setActiveTagInput(null);
                                    }
                                    if (e.key === "Escape") {
                                        setTagDraft("");
                                        setActiveTagInput(null);
                                    }
                                }}
                                onBlur={() => {
                                    const c = tagDraft.trim();
                                    if (c) onAddTag(id, c);
                                    setTagDraft("");
                                    setActiveTagInput(null);
                                }}
                                placeholder="Etiqueta..."
                                maxLength={20}
                            />
                        ) : (
                            <button
                                className="btn-add-tag-inline"
                                onClick={() => setActiveTagInput(id)}
                                title="Añadir etiqueta"
                            >
                                +
                            </button>
                        ))}
                </div>
                <div className="room-actions">
                    <button
                        className="btn-replay-room"
                        onClick={() => onReplayRoom(room)}
                        title="Recrear sesión"
                    >
                        <Icon {...ICONS.replay} />
                    </button>
                    <button
                        className={`btn-lock-room ${isPrivate ? "locked" : ""}`}
                        onClick={() => onToggleLock(room)}
                        title={isPrivate ? "Hacer pública" : "Hacer privada"}
                    >
                        <Icon {...(isPrivate ? ICONS.lock : ICONS.unlock)} />
                    </button>
                    <button
                        className={`btn-edit-lock ${locked ? "locked" : ""}`}
                        onClick={() => onToggleRoomLock(room.id)}
                        title={
                            locked ? "Desbloquear edición" : "Bloquear edición"
                        }
                    >
                        <Icon {...(locked ? ICONS.pencilOff : ICONS.pencil)} />
                    </button>
                    <button
                        className="btn-delete-room"
                        onClick={() => onDeleteRoom(room)}
                        title="Eliminar sala"
                    >
                        <Icon {...ICONS.trash} />
                    </button>
                </div>
            </div>

            <div className="student-list">
                {students.map((s) => (
                    <span className="student-chip" key={s}>
                        {s}
                        <button
                            onClick={() => onRemoveStudent(id, s)}
                            title="Eliminar"
                        >
                            ✕
                        </button>
                    </span>
                ))}
            </div>

            <div className="room-note-section">
                <button
                    className="room-note-toggle"
                    onClick={() =>
                        setCollapsedNotes?.((prev) => ({
                            ...prev,
                            [id]: !prev[id],
                        }))
                    }
                >
                    <Icon {...ICONS.note} size={14} />
                    <span>Notas</span>
                    <span
                        className="room-note-arrow"
                        style={{
                            transform: collapsedNotes?.[id]
                                ? "rotate(-90deg)"
                                : "rotate(0deg)",
                        }}
                    >
                        ▼
                    </span>
                </button>
                {collapsedNotes?.[id] && (
                    <div className="room-note">
                        <textarea
                            className="room-note-input"
                            placeholder="Escribe tus notas aquí..."
                            rows={3}
                            maxLength={500}
                            value={roomNotes[id] ?? notes ?? ""}
                            onChange={(e) =>
                                handleNoteChange(id, e.target.value)
                            }
                            onBlur={() => handleNoteBlur(id)}
                        />
                    </div>
                )}
            </div>

            {isPrivate ? (
                <>
                    {!locked && (
                        <div className="add-student-row">
                            <input
                                type="text"
                                value={newStudentInputs[id] ?? ""}
                                onChange={(e) =>
                                    handleStudentInputChange(id, e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAddStudent(id);
                                    }
                                }}
                                placeholder="Nombre del alumno"
                            />
                            <button
                                type="button"
                                className="btn-add-student"
                                onClick={() => handleAddStudent(id)}
                            >
                                Añadir
                            </button>
                        </div>
                    )}
                    {!locked && (
                        <button
                            type="button"
                            className="btn-import-secondary"
                            style={{ marginTop: "0.6rem" }}
                            onClick={() => openCsvImporter(id)}
                        >
                            Importar alumnos
                        </button>
                    )}
                    {locked && (
                        <div className="locked-notice">
                            <Icon {...ICONS.lock} size={14} /> Esta sala está
                            bloqueada. Los estudiantes no pueden modificarla.
                        </div>
                    )}
                </>
            ) : (
                <div className="public-notice">
                    <Icon {...ICONS.info} /> Cualquier persona con el código
                    puede entrar a esta sala
                </div>
            )}
        </div>
    );
});

const RoomDetail = ({ room, ...p }) => {
    const {
        name,
        isPrivate,
        tags = [],
        locked = false,
        students = [],
        notes = "",
    } = room;
    const {
        onDeleteRoom,
        onReplayRoom,
        onToggleLock,
        onToggleRoomLock,
        onRemoveTag,
        onMakeTagPrimary,
        onAddTag,
        tagDraft,
        onSetTagDraft,
        onRemoveStudent,
        activeTagInput,
        onSetActiveTagInput,
        newStudentInputs,
        roomNotes,
        handleNoteChange,
        handleNoteBlur,
        collapsedNotes,
        setCollapsedNotes,
    } = p;
    return (
        <div className="room-detail">
            <div className="room-detail-header">
                <div className="room-detail-title-row">
                    <h3 className="room-detail-title">{name}</h3>
                    <div className="room-detail-actions-inline">
                        <button
                            className="btn-replay-room"
                            onClick={() => onReplayRoom(room)}
                            title="Recrear sesión"
                        >
                            <Icon {...ICONS.replay} />
                        </button>
                        <button
                            className={`btn-lock-room ${isPrivate ? "locked" : ""}`}
                            onClick={() => onToggleLock(room)}
                            title={
                                isPrivate ? "Hacer pública" : "Hacer privada"
                            }
                        >
                            <Icon
                                {...(isPrivate ? ICONS.lock : ICONS.unlock)}
                            />
                        </button>
                        <button
                            className={`btn-edit-lock ${locked ? "locked" : ""}`}
                            onClick={() => onToggleRoomLock(room.id)}
                            title={
                                locked
                                    ? "Desbloquear edición"
                                    : "Bloquear edición"
                            }
                        >
                            <Icon
                                {...(locked ? ICONS.pencilOff : ICONS.pencil)}
                            />
                        </button>
                        <button
                            className="btn-delete-room"
                            onClick={() => onDeleteRoom(room)}
                            title="Eliminar sala"
                        >
                            <Icon {...ICONS.trash} />
                        </button>
                    </div>
                </div>
                <div className="room-detail-badges">
                    <span
                        className={`room-visibility-badge ${isPrivate ? "private" : "public"}`}
                    >
                        {isPrivate ? "Privada" : "Pública"}
                    </span>
                    {tags.map((t, i) => (
                        <TagChip
                            key={t}
                            tag={t}
                            isPrimary={i === 0}
                            roomId={room.id}
                            onRemove={onRemoveTag}
                            onMakePrimary={onMakeTagPrimary}
                        />
                    ))}
                    {tags.length < 5 &&
                        (activeTagInput === room.id ? (
                            <input
                                autoFocus
                                className="tag-inline-input"
                                value={tagDraft}
                                onChange={(e) => onSetTagDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        const c = tagDraft.trim();
                                        if (c) onAddTag(room.id, c);
                                        onSetTagDraft("");
                                        onSetActiveTagInput(null);
                                    }
                                    if (e.key === "Escape") {
                                        onSetTagDraft("");
                                        onSetActiveTagInput(null);
                                    }
                                }}
                                onBlur={() => {
                                    const c = tagDraft.trim();
                                    if (c) onAddTag(room.id, c);
                                    onSetTagDraft("");
                                    onSetActiveTagInput(null);
                                }}
                                placeholder="Etiqueta..."
                                maxLength={20}
                            />
                        ) : (
                            <button
                                className="btn-add-tag-inline"
                                onClick={() => onSetActiveTagInput(room.id)}
                            >
                                +
                            </button>
                        ))}
                </div>
            </div>

            <div className="student-list">
                {students.map((s) => (
                    <span className="student-chip" key={s}>
                        {s}
                        <button onClick={() => onRemoveStudent(room.id, s)}>
                            ✕
                        </button>
                    </span>
                ))}
            </div>

            <div className="room-note-section">
                <button
                    className="room-note-toggle"
                    onClick={() =>
                        setCollapsedNotes?.((prev) => ({
                            ...prev,
                            [room.id]: !prev[room.id],
                        }))
                    }
                >
                    <Icon {...ICONS.note} size={14} />
                    <span>Notas</span>
                    <span
                        className="room-note-arrow"
                        style={{
                            transform: collapsedNotes?.[room.id]
                                ? "rotate(-90deg)"
                                : "rotate(0deg)",
                        }}
                    >
                        ▼
                    </span>
                </button>
                {collapsedNotes?.[room.id] && (
                    <div className="room-note">
                        <textarea
                            className="room-note-input"
                            placeholder="Escribe tus notas aquí..."
                            rows={3}
                            maxLength={500}
                            value={roomNotes[room.id] ?? notes ?? ""}
                            onChange={(e) =>
                                handleNoteChange(room.id, e.target.value)
                            }
                            onBlur={() => handleNoteBlur(room.id)}
                        />
                    </div>
                )}
            </div>
            {isPrivate ? (
                <div className="room-detail-body">
                    <div className="add-student-row">
                        <input
                            type="text"
                            value={newStudentInputs[room.id] ?? ""}
                            onChange={(e) =>
                                handleStudentInputChange(
                                    room.id,
                                    e.target.value,
                                )
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddStudent(room.id);
                                }
                            }}
                            placeholder="Nombre del alumno..."
                        />
                        <button
                            type="button"
                            className="btn-add-student"
                            onClick={() => handleAddStudent(room.id)}
                        >
                            Añadir
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn-import-secondary"
                        style={{ marginTop: "0.6rem" }}
                        onClick={() => openCsvImporter(room.id)}
                    >
                        Importar alumnos
                    </button>
                </div>
            ) : (
                <div className="public-notice">
                    <Icon {...ICONS.info} /> Cualquier persona con el código
                    puede entrar a esta sala
                </div>
            )}
        </div>
    );
};

export default function RoomsModal({
    open,
    onClose,
    rooms,
    onCreateRoom,
    onDeleteRoom,
    onToggleLock,
    onToggleRoomLock,
    onAddStudent,
    onRemoveStudent,
    onAddTag,
    onRemoveTag,
    onMakeTagPrimary,
    roomsCsv,
    studentsCsv,
    onUpdateRoomNote,
    onReplayRoom,
}) {
    const [newRoomId, setNewRoomId] = useState("");
    const [studentInputs, setStudentInputs] = useState({});
    const [activeTagInput, setActiveTagInput] = useState(null);
    const [tagDraft, setTagDraft] = useState("");
    const [groupByTags, setGroupByTags] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [splitView, setSplitView] = useState(false);
    const [collapsed, setCollapsed] = useState({});
    const [searchQuery, setSearchQuery] = useState("");
    const [roomNotes, setRoomNotes] = useState({});
    const [collapsedNotes, setCollapsedNotes] = useState({});

    if (!open) return null;

    const query = searchQuery.trim().toLowerCase();
    const filtered = useMemo(
        () =>
            query
                ? rooms.filter((r) => {
                      const s = r.students || [],
                          t = r.tags || [];
                      return (
                          r.name.toLowerCase().includes(query) ||
                          t.some((x) => x.toLowerCase().includes(query)) ||
                          s.some((x) => x.toLowerCase().includes(query))
                      );
                  })
                : rooms,
        [rooms, query],
    );

    const safeRooms = filtered.filter((r) => r && typeof r === "object");

    const grouped = useMemo(() => {
        const sorted = [...safeRooms].sort((a, b) => {
            const ta = (a.tags?.[0] || "").toLowerCase(),
                tb = (b.tags?.[0] || "").toLowerCase();
            return ta === tb
                ? (a.name || "").localeCompare(b.name || "")
                : ta.localeCompare(tb);
        });
        return sorted.reduce((acc, r) => {
            const t = r.tags?.[0] || "Sin etiqueta";
            (acc[t] ??= []).push(r);
            return acc;
        }, {});
    }, [safeRooms]);

    const selectedRoom = safeRooms.find((r) => r.id === selectedRoomId);

    const createRoom = (e) => {
        e.preventDefault();
        const n = newRoomId.trim();
        if (n) {
            onCreateRoom(n);
            setNewRoomId("");
        }
    };
    const addStudent = (id) => {
        const n = (studentInputs[id] || "").trim();
        if (n) {
            onAddStudent(id, n);
            setStudentInputs((p) => ({ ...p, [id]: "" }));
        }
    };
    const setStudentInput = (id, v) =>
        setStudentInputs((p) => ({ ...p, [id]: v }));
    const openImporter = (id) => studentsCsv("students", id);
    const setNote = (id, v) => setRoomNotes((p) => ({ ...p, [id]: v }));
    const saveNote = (id) => {
        if (!(id in roomNotes)) return;
        const n = roomNotes[id];
        if (onUpdateRoomNote) onUpdateRoomNote(id, n);
    };

    const cardProps = {
        activeTagInput,
        tagDraft,
        setTagDraft,
        setActiveTagInput,
        onAddTag,
        onRemoveTag,
        onMakeTagPrimary,
        onToggleLock,
        onToggleRoomLock,
        onDeleteRoom,
        onRemoveStudent,
        handleStudentInputChange: setStudentInput,
        handleAddStudent: addStudent,
        openCsvImporter: openImporter,
        newStudentInputs: studentInputs,
        roomNotes,
        handleNoteChange: setNote,
        handleNoteBlur: saveNote,
        collapsedNotes,
        setCollapsedNotes,
        onReplayRoom,
    };

    const tagGroup = (tag, tagRooms, isList) => {
        const isCollapsed = collapsed[tag];
        const color = tag === "Sin etiqueta" ? "#a0aec0" : getTagColor(tag);
        return (
            <div
                key={tag}
                className={`room-group ${isList ? "list-group" : ""}`}
            >
                <h3
                    className="room-group-header"
                    onClick={() =>
                        setCollapsed((p) => ({ ...p, [tag]: !p[tag] }))
                    }
                    style={{
                        color,
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        margin: isList ? "0.5rem 0" : "1.5rem 0 0.75rem",
                        padding: "0.4rem 0.5rem",
                        borderBottom: `2px solid ${tag === "Sin etiqueta" ? "#a0aec0" : color + "30"}`,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderRadius: 4,
                        userSelect: "none",
                    }}
                >
                    <span>
                        {tag === "Sin etiqueta" ? "— Sin etiqueta —" : tag}
                        <span
                            style={{
                                marginLeft: "0.5rem",
                                opacity: 0.6,
                                fontSize: "0.75rem",
                            }}
                        >
                            ({tagRooms.length})
                        </span>
                    </span>
                    <span
                        style={{
                            fontSize: "0.7rem",
                            transition: "transform 0.2s",
                            transform: isCollapsed
                                ? "rotate(-90deg)"
                                : "rotate(0deg)",
                        }}
                    >
                        ▼
                    </span>
                </h3>
                {!isCollapsed &&
                    (isList
                        ? tagRooms.map((r) => (
                              <div
                                  key={r.id}
                                  className={`room-list-item ${selectedRoomId === r.id ? "selected" : ""}`}
                                  onClick={() => setSelectedRoomId(r.id)}
                              >
                                  <span className="room-list-name">
                                      {r.name}
                                  </span>
                                  <div className="room-list-tags">
                                      {r.tags?.slice(0, 3).map((t) => (
                                          <span
                                              key={t}
                                              className="room-list-tag-dot"
                                              style={{
                                                  backgroundColor:
                                                      getTagColor(t),
                                              }}
                                              title={t}
                                          />
                                      ))}
                                      {(r.tags?.length || 0) > 3 && (
                                          <span className="room-list-tag-more">
                                              +{r.tags.length - 3}
                                          </span>
                                      )}
                                  </div>
                              </div>
                          ))
                        : tagRooms.map((r) => (
                              <RoomCard key={r.id} room={r} {...cardProps} />
                          )))}
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-container rooms-modal-container"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header">
                    <h2>Mis salas</h2>
                    <button className="modal-close" onClick={onClose}>
                        ✕
                    </button>
                </div>
                <div className="modal-body">
                    <form className="create-room-row" onSubmit={createRoom}>
                        <button
                            type="button"
                            className={`btn-icon ${splitView ? "active" : ""}`}
                            onClick={() => {
                                setSplitView(!splitView);
                                if (!splitView) setSelectedRoomId(null);
                            }}
                            title={
                                splitView
                                    ? "Vista de tarjetas"
                                    : "Vista dividida"
                            }
                        >
                            <Icon {...ICONS.list} />
                        </button>
                        {!splitView && (
                            <button
                                type="button"
                                className={`btn-icon ${groupByTags ? "active" : ""}`}
                                onClick={() => setGroupByTags(!groupByTags)}
                                title={
                                    groupByTags
                                        ? "Ver lista plana"
                                        : "Agrupar por etiquetas"
                                }
                            >
                                <Icon {...ICONS.tag} />
                            </button>
                        )}
                        <input
                            type="text"
                            value={newRoomId}
                            onChange={(e) => setNewRoomId(e.target.value)}
                            placeholder="Nombre de la sala"
                            maxLength={20}
                        />
                        <button type="submit" className="btn-create">
                            Crear
                        </button>
                    </form>
                    <SearchBar q={searchQuery} onQ={setSearchQuery} />
                    {safeRooms.length === 0 ? (
                        <p className="empty-state">
                            {query
                                ? "No se encontró ninguna sala"
                                : "Aún no tienes salas creadas."}
                        </p>
                    ) : splitView ? (
                        <div className="rooms-split-view">
                            <div className="rooms-list-panel">
                                {Object.entries(grouped).map(([t, rs]) =>
                                    tagGroup(t, rs, true),
                                )}
                            </div>
                            <div className="rooms-detail-panel">
                                {selectedRoom ? (
                                    <RoomDetail
                                        room={selectedRoom}
                                        {...cardProps}
                                    />
                                ) : (
                                    <div className="room-detail-empty">
                                        Selecciona una sala para ver los
                                        detalles
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : groupByTags ? (
                        <div className="roomList">
                            {Object.entries(grouped).map(([t, rs]) =>
                                tagGroup(t, rs, false),
                            )}
                        </div>
                    ) : (
                        <div className="room-list">
                            {safeRooms.map((r) => (
                                <RoomCard key={r.id} room={r} {...cardProps} />
                            ))}
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn-import"
                        onClick={roomsCsv}
                        style={{ marginTop: "1rem" }}
                    >
                        Importar salas
                    </button>
                </div>
            </div>
        </div>
    );
}
